import { useEffect, useState } from 'react'
import KioskShell from '@/components/KioskShell'

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT

const COOKIE_KEY = 'kiosk_cfg'
const STORAGE_KEY = 'kiosk-config'

type KioskUrlConfig = { restaurant: string; till: string; tillName: string }

function parseParts(parts: string[]): KioskUrlConfig | null {
  if (parts.length >= 3) {
    return {
      restaurant: parts[0],
      till: parts[1],
      tillName: decodeURIComponent(parts[2].replace(/\+/g, ' ')),
    }
  }
  if (parts.length >= 2) {
    return { restaurant: parts[0], till: parts[1], tillName: '' }
  }
  return null
}

// Parse config from query parameter: ?c=restaurantId/tillId[/tillName]
function parseQuery(): KioskUrlConfig | null {
  if (typeof window === 'undefined') return null
  const c = new URLSearchParams(window.location.search).get('c')
  if (!c) return null
  return parseParts(c.split('/'))
}

// Legacy: parse config from hash: #restaurantId/tillId[/tillName]
function parseHash(): KioskUrlConfig | null {
  if (typeof window === 'undefined') return null
  const hash = window.location.hash.replace('#', '')
  if (!hash) return null
  return parseParts(hash.split('/'))
}

// Save config to BOTH cookie and localStorage for maximum PWA compatibility
// iOS PWA does not reliably share localStorage with Safari, but cookies are shared
function saveConfig(cfg: KioskUrlConfig) {
  const val = `${cfg.restaurant}/${cfg.till}`
  // Cookie: 1 year expiry, same-site, secure
  document.cookie = `${COOKIE_KEY}=${encodeURIComponent(val)};path=/;max-age=31536000;SameSite=Strict`
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg))
  } catch {
    /* localStorage unavailable (private mode etc.) */
  }
}

// Load config: try cookie first, then localStorage
function loadConfig(): KioskUrlConfig | null {
  // Try cookie
  const cookies = document.cookie.split(';')
  for (const c of cookies) {
    const [key, ...rest] = c.trim().split('=')
    if (key === COOKIE_KEY) {
      const val = decodeURIComponent(rest.join('='))
      const parsed = parseParts(val.split('/'))
      if (parsed) return parsed
    }
  }
  // Fallback to localStorage
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed?.restaurant && parsed?.till) return parsed
    }
  } catch {
    /* localStorage unavailable or contained invalid JSON */
  }
  return null
}

export default function App() {
  const [config, setConfig] = useState<KioskUrlConfig | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    // Priority: query param > hash > cookie/localStorage
    const parsed = parseQuery() || parseHash()

    if (parsed) {
      saveConfig(parsed)
      setConfig(parsed)
    } else {
      setConfig(loadConfig())
    }
    setReady(true)

    const handler = () => {
      const p = parseHash()
      if (p) {
        saveConfig(p)
        setConfig(p)
      }
    }
    window.addEventListener('hashchange', handler)
    return () => window.removeEventListener('hashchange', handler)
  }, [])

  if (!ready) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-kiosk-bg">
        <div className="kiosk-spinner" />
      </div>
    )
  }

  if (!config) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-kiosk-bg px-8 text-center">
        <h1 className="text-2xl font-bold text-red-400 mb-4">Kiosken är inte konfigurerad</h1>
        <p className="text-neutral-400 mb-6 max-w-md">Öppna kiosk-URL:en med konfiguration i Safari först, och lägg sedan till på hemskärmen.</p>
        <p className="text-neutral-500 text-sm">Format: .../?c=restaurangId/kassaId</p>
      </div>
    )
  }

  if (!API_ENDPOINT) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-kiosk-bg px-8 text-center">
        <h1 className="text-2xl font-bold text-red-400 mb-4">API-endpoint saknas</h1>
        <p className="text-neutral-400 mb-6 max-w-md">
          Miljövariabeln <code className="font-mono">VITE_API_ENDPOINT</code> är inte satt vid byggtid.
        </p>
      </div>
    )
  }

  return <KioskShell key={`${config.restaurant}-${config.till}`} restaurantId={config.restaurant} tillId={config.till} tillName={config.tillName} apiEndpoint={API_ENDPOINT} locale="sv" />
}
